import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Box,
  Divider,
  Button,
  TextField,
  Chip,
} from "@mui/material";

import api from "../services/api";

export default function Financeiro() {
  const [boletos, setBoletos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [ultimaBaixa, setUltimaBaixa] = useState(null);

  // 🔎 Filtros
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [buscaCliente, setBuscaCliente] = useState("");

  useEffect(() => {
    carregarBoletos();
    carregarUltimaBaixa();
  }, []);

  const carregarBoletos = async () => {
    setLoading(true);
    setErro("");

    try {
      const resp = await api.get("/financeiro/boletos-abertos");

      if (resp.status === 401 || resp.data?.message) {
        setErro(resp.data?.message || "Código do vendedor não encontrado.");
        setBoletos([]);
        setLoading(false);
        return;
      }

      let dados = [];
      
      if (Array.isArray(resp.data)) {
        dados = resp.data;
      } else if (resp.data && typeof resp.data === 'object' && Array.isArray(resp.data.boletos)) {
        dados = resp.data.boletos;
      } else if (resp.data && typeof resp.data === 'object') {
        console.warn("Resposta da API não é array:", resp.data);
        dados = [];
      }
      
      setBoletos(dados);
    } catch (e) {
      console.error("Erro ao carregar boletos:", e);
      
      if (e.response?.status === 401) {
        setErro(e.response?.data?.message || "Código do vendedor não encontrado no token.");
      } else {
        setErro("Não foi possível carregar os boletos.");
      }
      
      setBoletos([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarUltimaBaixa = async () => {
    try {
      const resp = await api.get("/financeiro/ultima-baixa");
      setUltimaBaixa(resp.data?.ultimaBaixa || null);
    } catch (e) {
      console.warn("Erro ao carregar última baixa", e);
    }
  };

  // =====================
  // 🔥 FUNÇÕES AUXILIARES
  // =====================
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const isProtestado = (valor) => {
    if (!valor) return false;
    const v = valor.toString().trim().toUpperCase();
    return v === "S" || v === "1" || v === "T" || v === "SIM";
  };

  const corBoleto = (vencimento) => {
    const v = new Date(vencimento);
    v.setHours(0, 0, 0, 0);

    if (v < hoje) return "#fdecea"; // vencido
    if (v.getTime() === hoje.getTime()) return "#fff4e5"; // vence hoje
    return "#e8f5e9"; // a vencer
  };

  // =====================
  // 🔥 FILTROS (BLINDADOS)
  // =====================
  const boletosSeguros = Array.isArray(boletos) ? boletos : [];
  
  const boletosFiltrados = boletosSeguros.filter((b) => {
    const venc = new Date(b.vencimento);
    venc.setHours(0, 0, 0, 0);

    if (filtroStatus === "vencidos" && venc >= hoje) return false;
    if (filtroStatus === "avencer" && venc < hoje) return false;

    if (
      buscaCliente &&
      !String(b.nomeCliente || "")
        .toLowerCase()
        .includes(buscaCliente.toLowerCase())
    )
      return false;

    return true;
  });

  // =====================
  // 🔥 AGRUPAR POR CLIENTE
  // =====================
  const boletosPorCliente = boletosFiltrados.reduce((acc, b) => {
    const cliente = b.nomeCliente || "Cliente não informado";
    if (!acc[cliente]) acc[cliente] = [];
    acc[cliente].push(b);
    return acc;
  }, {});

  // =====================
  // 🔥 TOTAIS
  // =====================
  const totalGeral = boletosFiltrados.reduce(
    (soma, b) => soma + Number(b.valorParcela || 0),
    0
  );

  const totalVencidos = boletosFiltrados
    .filter((b) => new Date(b.vencimento) < hoje)
    .reduce((s, b) => s + Number(b.valorParcela || 0), 0);

  const totalAVencer = boletosFiltrados
    .filter((b) => new Date(b.vencimento) >= hoje)
    .reduce((s, b) => s + Number(b.valorParcela || 0), 0);

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        Financeiro — Boletos em Aberto
      </Typography>

      {/* 🔎 FILTROS */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buscar cliente"
              value={buscaCliente}
              onChange={(e) => setBuscaCliente(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={1}>
              <Button
                variant={filtroStatus === "todos" ? "contained" : "outlined"}
                onClick={() => setFiltroStatus("todos")}
              >
                Todos
              </Button>
              <Button
                color="error"
                variant={filtroStatus === "vencidos" ? "contained" : "outlined"}
                onClick={() => setFiltroStatus("vencidos")}
              >
                Vencidos
              </Button>
              <Button
                color="success"
                variant={filtroStatus === "avencer" ? "contained" : "outlined"}
                onClick={() => setFiltroStatus("avencer")}
              >
                A vencer
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 📊 RESUMO */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: "#fdecea" }}>
            <Typography>Vencidos</Typography>
            <Typography variant="h6">
              R$ {totalVencidos.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: "#e8f5e9" }}>
            <Typography>A vencer</Typography>
            <Typography variant="h6">
              R$ {totalAVencer.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: "#f4f4f4" }}>
            <Typography>Total Geral</Typography>
            <Typography variant="h6">
              R$ {totalGeral.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 🔥 LISTAGEM */}
      {Object.entries(boletosPorCliente).map(([cliente, lista]) => {
        lista.sort(
          (a, b) => new Date(a.vencimento) - new Date(b.vencimento)
        );

        return (
          <Paper key={cliente} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6">{cliente}</Typography>
            <Divider sx={{ my: 2 }} />

            {lista.map((b) => {
              const venc = new Date(b.vencimento);
              venc.setHours(0, 0, 0, 0);
              const vencido = venc < hoje;
              const protestado = vencido && isProtestado(b.protesto);

              return (
                <Box
                  key={b.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: corBoleto(b.vencimento),
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {protestado && (
                    <Chip
                      label="PROTESTADO"
                      color="error"
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}

                  <Box flexGrow={1}>
                    <Typography variant="body2">
                      NF: {b.notaFiscal || "-"} | Parcela: {b.parcela}
                    </Typography>
                    <Typography variant="body2">
                      Vencimento:{" "}
                      {new Date(b.vencimento).toLocaleDateString("pt-BR")}
                    </Typography>
                  </Box>

                  <Typography variant="body2">
                    <strong>
                      R$
                      {Number(b.valorParcela || 0).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </strong>
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        );
      })}

      {loading && (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {erro && (
        <Typography color="error" mt={3}>
          {erro}
        </Typography>
      )}

      {!loading && boletosFiltrados.length === 0 && (
        <Typography mt={3}>
          Nenhum boleto encontrado para os filtros selecionados.
        </Typography>
      )}
    </Container>
  );
}
